import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import styles from './UploadHistory.module.scss';
import cx from 'classnames';
import { FILE_ACTION, FILE_STATUS } from '../../../types';
import { Button } from '@navi/web-ui/lib/primitives';
import { DownloadIcon } from '@navi/web-ui/lib/icons';
import LoaderIcon from '@cp/src/assets/icons/LoaderIcon';
import ErrorIconOutline from '@cp/src/assets/icons/ErrorIconOutline';
import { fetchFile } from '../../../AmeyoUtilityActions';
import { useDispatch } from 'react-redux';
import { useMemo } from 'react';

export const useColumnDefs = (): {
  columnDefs: ColDef[];
  handleFileButtonClick: (fileReferenceId: string, status: string) => void;
  handleFailureReportButtonClick: (fileReferenceId: string, failureReportExists: boolean) => void;
} => {
  const dispatch = useDispatch();
  const handleFileButtonClick = (fileReferenceId: string, status: string) => {
    if (status === FILE_STATUS.COMPLETED || status === FILE_STATUS.FAILED) {
      dispatch(fetchFile(fileReferenceId, FILE_ACTION.ORIGINAL));
    }
  };
  const handleFailureReportButtonClick = (
    fileReferenceId: string,
    failureReportExists: boolean
  ) => {
    if (failureReportExists) {
      dispatch(fetchFile(fileReferenceId, FILE_ACTION.ERRORS));
    }
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'fileName',
        headerName: 'File Name',
        wrapHeaderText: true,
        minWidth: 100,
        maxWidth: 150,
        wrapText: true,
        headerComponentParams: { width: 150 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          return (
            <div>
              <Tooltip placement="bottom" hiddenPadding={0} hideStrategy="referenceHidden">
                <TooltipTrigger>
                  <div className={styles.fileName}>{data?.fileName}</div>
                </TooltipTrigger>
                <TooltipContent
                  className={cx(styles.tooltipWrapper, styles.uploadHistoryTooltipContent)}
                >
                  {data?.fileName}
                </TooltipContent>
              </Tooltip>
            </div>
          );
        }
      },
      {
        field: 'uploaded',
        headerName: 'Uploaded',
        minWidth: 80,
        maxWidth: 90,
        headerComponentParams: { width: 100 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (
            data?.status === FILE_STATUS.INITIATED ||
            data?.status === FILE_STATUS.FAILED ||
            data?.status === FILE_STATUS.INPROGRESS
          ) {
            return '-';
          }
          return <span>{data?.uploaded}</span>;
        }
      },
      {
        field: 'failed',
        headerName: 'Failed',
        minWidth: 50,
        maxWidth: 70,
        headerComponentParams: { width: 100 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          if (
            data?.status === FILE_STATUS.INITIATED ||
            data?.status === FILE_STATUS.FAILED ||
            data?.status === FILE_STATUS.INPROGRESS
          ) {
            return '-';
          }
          const textColor = data?.failureReportExists ? 'var(--red-base)' : '#var(--grayscale-2)';
          return <span style={{ color: textColor }}>{data?.failed}</span>;
        }
      },
      {
        field: 'uploadDateTime',
        headerName: 'Upload Date & Time',
        minWidth: 150,
        maxWidth: 200,
        wrapHeaderText: false,
        headerComponentParams: { width: 150 },
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          const textColor = 'var(--grayscale-3';
          return <span style={{ color: textColor }}>{data.uploadDateTime}</span>;
        }
      },
      {
        field: 'file',
        headerName: 'Original File',
        minWidth: 100,
        maxWidth: 130,
        headerComponentParams: { width: 100 },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          const textColor =
            data?.status === (FILE_STATUS.INPROGRESS || data?.status === FILE_STATUS.INITIATED)
              ? 'var(--green-base)'
              : data?.status === FILE_STATUS.COMPLETED
              ? 'var(--blue-base)'
              : 'var(--red-base)';
          const icon =
            data?.status === FILE_STATUS.COMPLETED ? (
              <DownloadIcon color="var(--blue-base)" />
            ) : data?.status === FILE_STATUS.FAILED ? (
              <ErrorIconOutline />
            ) : (
              <LoaderIcon
                size={20}
                borderWidth={3}
                borderColor="var(--greyscale-light)"
                fillColor="var(--green-base)"
              />
            );
          const cursorStyle =
            data?.status === FILE_STATUS.INPROGRESS ||
            data?.status === FILE_STATUS.INITIATED ||
            data?.status === FILE_STATUS.FAILED
              ? 'auto'
              : 'pointer';
          return (
            <Button
              variant="text"
              className={styles.buttonWrapper}
              style={{ color: textColor, cursor: cursorStyle }}
              onClick={() => handleFileButtonClick(data.file, data.status)}
              startAdornment={data?.status && icon}
            >
              <span className={styles.buttonText}>
                {data?.status && data?.status === 'COMPLETED'
                  ? 'Download'
                  : data?.status === 'FAILED'
                  ? 'Failed'
                  : 'In Progress'}
              </span>
            </Button>
          );
        }
      },
      {
        field: 'failureReport',
        headerName: 'Failure Report',
        wrapHeaderText: false,
        minWidth: 100,
        maxWidth: 130,
        headerComponentParams: { width: 80 },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          const { failureReportExists } = data;
          if (failureReportExists) {
            return (
              <Button
                variant="text"
                className={styles.buttonWrapper}
                onClick={() => handleFailureReportButtonClick(data.file, data.failureReportExists)}
                startAdornment={<DownloadIcon color="var(--blue-base)" />}
              >
                <span className={styles.buttonText}>Download</span>
              </Button>
            );
          }
          return null;
        }
      }
    ],
    []
  );
  return { columnDefs, handleFileButtonClick, handleFailureReportButtonClick };
};

export default useColumnDefs;
